import React, { useCallback, useMemo, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { ModalFooter } from '../../../components/ModalFooter';
import { StepWizard } from '../../../components/StepWizard';
import { CreditCardModal } from '../../../components/modals/CreditCardModal';
import { getAccountService } from '../../../services/angular-bridge';
import { useCreateExport, useExports, type Export, type ExportStatistics } from '../../../hooks/useExports';
import { useIsInternalUser } from '../../../hooks/useIsInternalUser';
import { useLoadFileFormats, useSaveLoadFileFormat, type LoadFileFormat } from '../../../hooks/useLoadFileFormats';
import { useProjectIdFromRoute } from '../../../hooks/useProjectIdFromRoute';
import { useRelativitySettingsList, useSaveRelativitySettings } from '../../../hooks/useRelativitySettings';
import { isOrganizationProject } from '../../../types/project';
import {
  ENDORSEMENT_TYPE,
  type EndorsementItem,
  type EndorsementTypeValue,
  ExportFormState,
  initialFormState,
  OUTPUT_TYPE,
} from '../../../types/export';
import type { RelativitySettingsItem } from '../../../types/relativitySettings';
import { Step1Data } from './Step1Data';
import { Step2Output, type ExportDestination } from './Step2Output';
import { Step2_5ClientStorage } from './Step2_5ClientStorage';
import { Step2_1LoadFile } from './Step2_1LoadFile';
import { Step2_1RelativitySettings } from './Step2_1RelativitySettings';
import { Step3Naming } from './Step3Naming';
import { Step4Summary } from './Step4Summary';

// API returns numeric endorsement types (0=None, 1=Bates, 2=Text, 3=Tag)
const NUMERIC_ENDORSEMENT_MAP: Record<number, EndorsementTypeValue> = {
  0: ENDORSEMENT_TYPE.None,
  1: ENDORSEMENT_TYPE.Bates,
  2: ENDORSEMENT_TYPE.Text,
  3: ENDORSEMENT_TYPE.Tag,
};

function inferEndorsementType(items: { id: string; type?: number | string }[] | undefined): EndorsementTypeValue {
  if (!items || items.length === 0) return ENDORSEMENT_TYPE.None;
  const { id, type } = items[0];
  if (id === 'Bates') return ENDORSEMENT_TYPE.Bates;
  if (typeof type === 'number') return NUMERIC_ENDORSEMENT_MAP[type] ?? ENDORSEMENT_TYPE.None;
  if (typeof type === 'string' && (Object.values(ENDORSEMENT_TYPE) as string[]).includes(type)) {
    return type as EndorsementTypeValue;
  }
  return ENDORSEMENT_TYPE.Text;
}

function mapEndorsementItems(
  items: { id: string; name?: string; value?: string; type?: number | string }[] | undefined,
  type: EndorsementTypeValue,
): EndorsementItem[] {
  if (!items || items.length === 0 || type === ENDORSEMENT_TYPE.None) return [];
  return items.map(e => ({ id: e.id, name: e.name ?? e.value ?? '', type }));
}

const BLANK_LOAD_FILE_FORMAT: LoadFileFormat = {
  type: 'user',
  subType: 'data',
  name: '',
  fileExtension: 'DAT',
  fields: [],
  encoding: 'utf8',
  hasHeaderRow: true,
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'hh:mm:ss a',
  dateTimeFormat: 'MM/dd/yyyy hh:mm:ss a',
  prependPath: null,
  pathSeparator: '\\',
  columnDelimiterCharCode: 20,
  quoteCharCode: 254,
  multiValueCharCode: 59,
  nestedValuesCharCode: 92,
  embeddedNewlineCharCode: 174,
  includeMultiValueFieldConfig: false,
  newline: { type: 'windows' },
};

interface StepValidity {
  step1: boolean;
  step2: boolean;
  step3: boolean;
}

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  project: any;
  onClose: () => void;
}

export function ExportModal({ project, onClose }: Props) {
  const projectId = useProjectIdFromRoute();
  // Client Storage upload requires the `exportToClientStorage` feature flag and a project in
  // an Organization (same org gate as Direct to Relativity). When available it is the default
  // destination; otherwise the export falls back to a download link and the option is hidden
  // in Step 2.
  const clientStorageEnabled =
    isOrganizationProject(project) && !!(window as any).__features?.exportToClientStorage;
  const [formState, setFormState] = useState<ExportFormState>(() => ({
    ...initialFormState(projectId),
    clientStorageUpload: clientStorageEnabled,
  }));
  const [step, setStep] = useState(1);
  const [nativeEnabled, setNativeEnabled] = useState(false);
  const [nativeType, setNativeType] = useState<'Native' | 'NativeWithPlaceholder'>('Native');
  const [stepValidity, setStepValidity] = useState<StepValidity>({
    step1: false,
    step2: false,
    step3: false,
  });
  const [exportStats, setExportStats] = useState<ExportStatistics | null>(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [warningsAccepted, setWarningsAccepted] = useState(false);

  const [editingFormat, setEditingFormat] = useState<LoadFileFormat | null>(null);
  const [editingRelativitySettings, setEditingRelativitySettings] = useState<RelativitySettingsItem | null>(null);

  const isInternalUser = useIsInternalUser();

  const directToRelativityEnabled = useMemo(() => {
    try {
      const email = getAccountService().GetEmail()?.toLowerCase() ?? '';
      const flagEnabled = !!(window as any).__features?.exportToRelativity;
      return isOrganizationProject(project) && (
        flagEnabled
          ? isInternalUser
          : email === 'austin.smith-bartlett@innovativedriven.com'
      );
    } catch {
      return false;
    }
  }, [project, isInternalUser]);

  const handleStep1Valid = useCallback((v: boolean) => setStepValidity(p => ({ ...p, step1: v })), []);
  const handleStep2Valid = useCallback((v: boolean) => setStepValidity(p => ({ ...p, step2: v })), []);
  const handleStep3Valid = useCallback((v: boolean) => setStepValidity(p => ({ ...p, step3: v })), []);

  const createExport = useCreateExport(projectId);
  const saveLoadFileFormat = useSaveLoadFileFormat(projectId);
  const saveRelativitySettings = useSaveRelativitySettings(projectId);
  const { data: loadFileFormats = [] } = useLoadFileFormats(projectId);
  const { data: exports_ = [] } = useExports(projectId);
  const { data: relativitySettingsList = [] } = useRelativitySettingsList(projectId, directToRelativityEnabled);

  const handleImportTemplate = useCallback(
    (exportId: string) => {
      const tmpl = exports_.find((e: Export) => e.id === exportId);
      if (!tmpl) return;

      // API returns "Native with Placeholder"; form constant is "NativeWithPlaceholder"
      const normalizedOutputTypes = (tmpl.outputTypes ?? []).map(t =>
        t === 'Native with Placeholder' ? OUTPUT_TYPE.NativeWithPlaceholder : t,
      );
      const hasNativeWithPlaceholder = normalizedOutputTypes.includes(OUTPUT_TYPE.NativeWithPlaceholder);
      const hasNative = normalizedOutputTypes.includes(OUTPUT_TYPE.Native);
      const nativeInTemplate = hasNative || hasNativeWithPlaceholder;
      setNativeEnabled(nativeInTemplate);
      setNativeType(hasNativeWithPlaceholder ? 'NativeWithPlaceholder' : 'Native');

      setFormState(prev => ({
        ...prev,
        collectionIds: tmpl.collectionIds ?? prev.collectionIds,
        collectionNames: tmpl.collectionNames ?? prev.collectionNames,
        loadFileFormatIds: tmpl.loadFileFormatIds ?? prev.loadFileFormatIds,
        loadFileFormatNames: tmpl.loadFileFormatNames ?? prev.loadFileFormatNames,
        tags: tmpl.tags
          ? { and: tmpl.tags.and, or: tmpl.tags.or, not: tmpl.tags.not }
          : prev.tags,
        outputTypes: normalizedOutputTypes.length > 0 ? normalizedOutputTypes : prev.outputTypes,
        colorSetting: tmpl.colorSetting ?? prev.colorSetting,
        colorTag: tmpl.colorTag ?? prev.colorTag,
        prefix: tmpl.prefix ?? prev.prefix,
        delimiter: tmpl.delimiter ?? prev.delimiter,
        startValue: tmpl.startValue ?? prev.startValue,
        zeroPaddingLength: tmpl.zeroPaddingLength ?? prev.zeroPaddingLength,
        filesPerDirectory: tmpl.filesPerDirectory ?? prev.filesPerDirectory,
        outputOriginalNativePaths: tmpl.outputOriginalNativePaths ?? prev.outputOriginalNativePaths,
        keepPageOrientation: tmpl.keepPageOrientation ?? prev.keepPageOrientation,
        spreadsheetPlaceholder: tmpl.spreadsheetPlaceholder ?? prev.spreadsheetPlaceholder,
        presentationPlaceholder: tmpl.presentationPlaceholder ?? prev.presentationPlaceholder,
        virusPlaceholderText: tmpl.virusPlaceholderText ?? prev.virusPlaceholderText,
        nativeFormatPlaceholderText: tmpl.nativeFormatPlaceholderText ?? prev.nativeFormatPlaceholderText,
        failedToImagePlaceholderText: tmpl.failedToImagePlaceholderText ?? prev.failedToImagePlaceholderText,
        includeRedactedResources: tmpl.includeRedactedResources ?? prev.includeRedactedResources,
        includeDuplicateResources: tmpl.includeDuplicateResources ?? prev.includeDuplicateResources,
        includeResourcesWithProcessingExceptions:
          tmpl.includeResourcesWithProcessingExceptions ?? prev.includeResourcesWithProcessingExceptions,
        includeResourcesWithZeroPages: tmpl.includeResourcesWithZeroPages ?? prev.includeResourcesWithZeroPages,
        tagPlaceholderEnabled: tmpl.tagPlaceholderEnabled ?? prev.tagPlaceholderEnabled,
        tagPlaceholder: tmpl.tagPlaceholder
          ? { id: tmpl.tagPlaceholder.id, name: tmpl.tagPlaceholder.name }
          : prev.tagPlaceholder,
        containerExportTag: tmpl.containerExportTag
          ? { id: tmpl.containerExportTag.id, name: tmpl.containerExportTag.name }
          : prev.containerExportTag,
        includeContainerExportTag: tmpl.includeContainerExportTag ?? prev.includeContainerExportTag,
        southWestEndorsement: tmpl.southWestEndorsement != null
          ? mapEndorsementItems(tmpl.southWestEndorsement, inferEndorsementType(tmpl.southWestEndorsement))
          : prev.southWestEndorsement,
        southEndorsement: tmpl.southEndorsement != null
          ? mapEndorsementItems(tmpl.southEndorsement, inferEndorsementType(tmpl.southEndorsement))
          : prev.southEndorsement,
        southEastEndorsement: tmpl.southEastEndorsement != null
          ? mapEndorsementItems(tmpl.southEastEndorsement, inferEndorsementType(tmpl.southEastEndorsement))
          : prev.southEastEndorsement,
      }));
    },
    [exports_],
  );

  // Sync outputTypes when native state changes
  const handleNativeChange = useCallback(
    (enabled: boolean, type: 'Native' | 'NativeWithPlaceholder') => {
      setNativeEnabled(enabled);
      setNativeType(type);
      setFormState(prev => {
        const without = prev.outputTypes.filter(
          t => t !== OUTPUT_TYPE.Native && t !== OUTPUT_TYPE.NativeWithPlaceholder,
        );
        return {
          ...prev,
          outputTypes: enabled ? [...without, OUTPUT_TYPE[type]] : without,
        };
      });
    },
    [],
  );

  const handleSubmit = useCallback(() => {
    // formState holds the full tag under `colorTag`; the backend expects it under `colorTagId`.
    const { colorTag, ...rest } = formState;
    const payload = {
      ...rest,
      tags: {
        and: formState.tags.and.map(t => t.id),
        or: formState.tags.or.map(t => t.id),
        not: formState.tags.not.map(t => t.id),
      },
      colorTagId: colorTag || null,
      southWestEndorsement: formState.southWestEndorsement.map(e => ({ id: e.id, value: e.name, type: e.type })),
      southEndorsement: formState.southEndorsement.map(e => ({ id: e.id, value: e.name, type: e.type })),
      southEastEndorsement: formState.southEastEndorsement.map(e => ({ id: e.id, value: e.name, type: e.type })),
      outputTypes: formState.directToRelativity ? [OUTPUT_TYPE.Text, OUTPUT_TYPE.Native] : formState.outputTypes,
      loadFileFormatIds: formState.directToRelativity ? [formState.relativitySettingsId] : formState.loadFileFormatIds,
    };
    createExport.mutate(payload, { onSuccess: onClose });
  }, [formState, createExport, onClose]);

  // Single source of truth for the export destination. The three choices are mutually
  // exclusive, so each clears the other's fields.
  const handleDestinationChange = useCallback((dest: ExportDestination) => {
    setFormState(prev => {
      switch (dest) {
        case 'ClientStorage':
          return { ...prev, clientStorageUpload: true, directToRelativity: false, relativitySettingsId: '' };
        case 'Relativity':
          return { ...prev, directToRelativity: true, clientStorageUpload: false, clientStorageUploadDirectory: '', clientStorageBucketName: '' };
        default:
          return { ...prev, clientStorageUpload: false, directToRelativity: false, relativitySettingsId: '', clientStorageUploadDirectory: '', clientStorageBucketName: '' };
      }
    });
  }, []);

  const goBack = useCallback(() => setStep(s => s - 1), []);

  if (project?.status === 'trial') {
    return <CreditCardModal projectId={projectId} onClose={onClose} />;
  }

  const BLANK_RELATIVITY_SETTINGS: RelativitySettingsItem = {
    name: '', workspace: undefined, hasHeaderRow: false,
    selectMode: [], overlayIdentifier: [], multiSelect: [], rootFolder: [], subFolder: [], fields: [],
  };

  if (editingFormat !== null) {
    const lfValid = !!editingFormat.name && editingFormat.name.length <= 255 &&
      !!editingFormat.fileExtension && editingFormat.fileExtension.length <= 255 &&
      !!editingFormat.pathSeparator;
    return (
      <Modal
        title="Export Setup"
        icon="fas fa-share"
        onClose={onClose}
        large
        subHeader={<p className="lead">Customize Load File Format</p>}
        testId="export-wizard-load-file-modal"
        onSubmit={() => saveLoadFileFormat.mutate(editingFormat, { onSuccess: () => setEditingFormat(null) })}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setEditingFormat(null)} disabled={saveLoadFileFormat.isPending} data-testid="export-wizard-load-file-cancel-btn">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!lfValid || saveLoadFileFormat.isPending} data-testid="export-wizard-load-file-save-btn">Save</button>
          </>
        }
      >
        <Step2_1LoadFile
          editFormat={editingFormat}
          onChange={setEditingFormat}
          loadFileFormats={loadFileFormats}
          saving={saveLoadFileFormat.isPending}
        />
      </Modal>
    );
  }

  if (editingRelativitySettings !== null) {
    const rsValid = !!editingRelativitySettings.name && editingRelativitySettings.name.length <= 255;
    return (
      <Modal
        title="Export Setup"
        icon="fas fa-share"
        onClose={onClose}
        large
        subHeader={<p className="lead">Customize Relativity Settings</p>}
        testId="export-wizard-relativity-modal"
        onSubmit={() => saveRelativitySettings.mutate(editingRelativitySettings, { onSuccess: () => setEditingRelativitySettings(null) })}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setEditingRelativitySettings(null)} disabled={saveRelativitySettings.isPending} data-testid="export-wizard-relativity-cancel-btn">Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={!rsValid || saveRelativitySettings.isPending} data-testid="export-wizard-relativity-save-btn">Save</button>
          </>
        }
      >
        <Step2_1RelativitySettings
          settings={editingRelativitySettings}
          onChange={setEditingRelativitySettings}
          saving={saveRelativitySettings.isPending}
        />
      </Modal>
    );
  }

  // Wizard steps, built dynamically so the Client Storage step only appears when
  // "Save to Client Storage" is selected in Step 2. StepWizard renders children by
  // index, and Children.toArray drops the falsy conditional child below, so this list
  // stays aligned with the rendered children.
  const steps = [
    { key: 'data', label: 'Select Data', subHeader: 'Select your Data', valid: stepValidity.step1 },
    { key: 'output', label: 'Output Options', subHeader: 'Output Options', valid: stepValidity.step2 },
    ...(formState.clientStorageUpload
      ? [{ key: 'storage', label: 'Storage Location', subHeader: 'Storage Location', valid: !!formState.clientStorageUploadDirectory }]
      : []),
    { key: 'naming', label: 'Naming', subHeader: 'Naming', valid: stepValidity.step3 },
    { key: 'summary', label: 'Summary', subHeader: 'Summary', valid: true },
  ];
  const totalSteps = steps.length;
  const current = steps[step - 1] ?? steps[totalSteps - 1];
  const isSummaryStep = step === totalSteps;

  const subHeader = <p className="lead">{current.subHeader}</p>;

  const footer = !isSummaryStep ? (
    <ModalFooter
      onClose={onClose}
      onBack={step > 1 ? goBack : undefined}
      onNext={() => setStep(s => s + 1)}
      nextDisabled={!current.valid}
    />
  ) : (
    <ModalFooter
      onClose={onClose}
      onBack={() => setStep(totalSteps - 1)}
      saving={createExport.isPending}
      savingLabel="Initiating Export..."
      nextLabel="Start Export"
    />
  );

  return (
    <Modal
      title={<>Export Setup <small>Step {step} of {totalSteps}</small></>}
      icon="fas fa-share"
      onClose={onClose}
      large
      footer={footer}
      onSubmit={isSummaryStep ? handleSubmit : undefined}
      subHeader={subHeader}
      scrollResetKey={step}
      testId="export-wizard-modal"
    >
      <StepWizard currentStep={step} testId="export-wizard-steps-panel">
        <Step1Data
          formState={formState}
          onChange={patch => setFormState(prev => ({ ...prev, ...patch }))}
          exports={exports_}
          onImportTemplate={handleImportTemplate}
          onValidChange={handleStep1Valid}
          onStatsChange={setExportStats}
          acceptTerms={acceptTerms}
          onAcceptTermsChange={setAcceptTerms}
          warningsAccepted={warningsAccepted}
          onWarningsAcceptedChange={setWarningsAccepted}
        />
        <Step2Output
          formState={formState}
          onChange={patch => setFormState(prev => ({ ...prev, ...patch }))}
          nativeEnabled={nativeEnabled}
          nativeType={nativeType}
          onNativeEnabledChange={enabled => handleNativeChange(enabled, nativeType)}
          onNativeTypeChange={type => handleNativeChange(nativeEnabled, type)}
          loadFileFormats={loadFileFormats as LoadFileFormat[]}
          onEditLoadFileFormat={format => setEditingFormat(format ?? BLANK_LOAD_FILE_FORMAT)}
          onValidChange={handleStep2Valid}
          relativitySettingsList={relativitySettingsList}
          onEditRelativitySettings={s => setEditingRelativitySettings(s ?? BLANK_RELATIVITY_SETTINGS)}
          clientStorageEnabled={clientStorageEnabled}
          directToRelativityEnabled={directToRelativityEnabled}
          onDestinationChange={handleDestinationChange}
        />
        {formState.clientStorageUpload && (
          <Step2_5ClientStorage
            onChange={patch => setFormState(prev => ({ ...prev, ...patch }))}
            projectId={projectId}
          />
        )}
        <Step3Naming
          formState={formState}
          onChange={patch => setFormState(prev => ({ ...prev, ...patch }))}
          onValidChange={handleStep3Valid}
        />
        <Step4Summary
          formState={formState}
          stats={exportStats}
        />
      </StepWizard>
    </Modal>
  );
}
